import { Component } from '@verndale/core';
import { rovingIndex } from 'roving-ux';
import { dispatchChangeTab } from '../coveo/modules/contractor-listing/helpers';
import Cookies from 'cookies-ts';
import { COOKIE_NAMES } from '../helpers/global-storage';
import { PERSONA_COOKIE } from '../react-components/document-listing/constants';

class TabbedContainer extends Component {
  private defaultValue: string | null;
  private panels: object;
  private firstLoad: boolean;

  constructor(el: HTMLElement) {
    super(el);
    this.defaultValue = this.el.dataset.defaultValue || null;
    this.panels = this.setTabPanels();
    this.firstLoad = true;
  }

  setupDefaults() {
    this.dom = {
      container: this.el,
      tabsList: this.el.querySelector<HTMLElement>(
        '.tabbed-container__tabs-list'
      ),
      tabs: this.el.querySelectorAll<HTMLButtonElement>(
        '.tabbed-container__tab-btn'
      ),
      panels: this.el.querySelectorAll<HTMLElement>('.tabbed-container__panel'),
      dropdown: this.el.querySelector<HTMLFormElement>('.field__select'),
      panelsContainer: this.el.querySelector<HTMLElement>(
        '.tabbed-container__content-wrapper'
      )
    };

    rovingIndex({
      element: this.dom.tabsList,
      target: '.tabbed-container__tab-btn'
    });


    const commercialPersonas = [
      "Commercial Contractor",
      "Architect / Design Pro",
      "Building Manager / Owner"
    ];
    const cookies = new Cookies();
    const personaCookie = cookies.get(COOKIE_NAMES.persona);
    const pathname = window.location.pathname;
    const trimmedPathname = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    const segments = trimmedPathname.split('/');
    const lastSegment = segments[segments.length - 1];


    if (lastSegment === PERSONA_COOKIE.commercial) {
      this.defaultValue = PERSONA_COOKIE.commercial;
    } else if (lastSegment === PERSONA_COOKIE.residential) {
      this.defaultValue = PERSONA_COOKIE.residential
    } else if (commercialPersonas.includes(personaCookie || '')) {
      this.defaultValue = PERSONA_COOKIE.commercial;
      this.updateUrlState();
    } else {
      this.defaultValue = PERSONA_COOKIE.residential;
      if(personaCookie) {
        this.updateUrlState();
      }
    }
    this.panels = this.setTabPanels();

    this.init();
  }

  updateUrlState() {
    const redirectValues = this.el.dataset.redirectValues;
      if (redirectValues) {
        const redirectValuesObject = JSON.parse(redirectValues);
        const match = redirectValuesObject.find((item: any) => item.value === this.defaultValue);
        if (match) {
          const url = new URL(match.url, window.location.origin);
          url.protocol = window.location.protocol; // force https if current page is https
          window.history.replaceState(null, "", url.href);
        }
      }
  }

  addListeners() {
    window.addEventListener('documentLandingChange', e =>
      this.handlePersonaCommercial(e as CustomEvent)
    );
    (this.dom.tabs as NodeList).forEach(trigger => {
      trigger.addEventListener('click', this.handleTabClick.bind(this));
    });

    (this.dom.dropdown as HTMLFormElement)?.addEventListener(
      'change',
      this.handleDropdownChange.bind(this)
    );
  }

  init() {
    if (this.defaultValue) {
      const defaultTab = this.el.querySelector<HTMLElement>(
        `.tabbed-container__tab-btn[data-value="${this.defaultValue}"]`
      );
      if (defaultTab) {
        this.setActiveTab(defaultTab);
      }
    } else if (this.dom.tabs) {
      this.setActiveTab(
        (this.dom.tabs as NodeListOf<HTMLButtonElement>).item(0)
      );
    }
  }

  setTabPanels() {
    let panels = {};

    (this.dom.tabs as NodeListOf<HTMLElement>)?.forEach(tab => {
      const value = tab.dataset.value;
      const id = tab.id;
      const panel = this.el.querySelector<HTMLElement>(
        `[aria-labelledby="${id}"]`
      );

      if (value) {
        panels = { ...panels, [value]: panel };
      }
    });

    return panels;
  }

  setActiveTab(button: HTMLElement) {
    const activeTab = button;
    const activePanel = activeTab?.dataset?.value;

    if (activePanel) {
      (this.dom.panels as NodeListOf<HTMLElement>).forEach(panel => {
        panel.classList.add('tabbed-container__panel--hidden');
        panel.classList.remove('.active');
        panel.tabIndex = -1;
      });

      (this.dom.tabs as NodeListOf<HTMLElement>).forEach(tab => {
        tab.classList.remove('active');
        tab.ariaSelected = 'false';
      });

      const currentPanel: HTMLElement =
        this.panels[activePanel as keyof object];

      currentPanel?.classList.remove('tabbed-container__panel--hidden');
      currentPanel?.classList.add('active');
      currentPanel.tabIndex = 0;

      activeTab.classList.add('active');
      activeTab.setAttribute('aria-selected', 'true');

      if (this.firstLoad) {
        activeTab.focus();
        this.firstLoad = false;
      }

      if (this.dom.dropdown)
        (this.dom.dropdown as HTMLFormElement).value = activePanel;
    }
  }

  redirectToValue(selectedValue: string) {
    const redirectValues = this.el.dataset.redirectValues;

    if (redirectValues) {
      const redirectValuesObject = JSON.parse(redirectValues);
      const redirectValue = redirectValuesObject.find(
        (value: { value: string; url: string }) => value.value === selectedValue
      );
      if (redirectValue) {
        window.location.href = redirectValue.url;
      }
    }
  }

  handlePersonaCommercial(e: CustomEvent) {
    if (e.detail) {
      this.setActiveTab(
        (this.dom.tabs as NodeListOf<HTMLButtonElement>).item(1)
      );
    } else {
      this.setActiveTab(
        (this.dom.tabs as NodeListOf<HTMLButtonElement>).item(0)
      );
    }
  }

  handleTabClick(e: Event) {
    const target = e.target as HTMLElement;
    if (this.el.closest('.city-listing-masthead')) {
      dispatchChangeTab(target.innerText);
    }
    this.setActiveTab(target);

    if (target.dataset.value) {
      this.redirectToValue(target.dataset.value);
    }
  }

  handleDropdownChange(e: Event) {
    const target = e.target as HTMLFormElement;
    const value = target.value;
    const tab = this.el.querySelector<HTMLElement>(
      `.tabbed-container__tab-btn[data-value="${value}"]`
    );
    if (this.el.closest('.city-listing-masthead') && tab) {
      dispatchChangeTab(tab.innerText?.trim());
    }
    if (tab) {
      this.setActiveTab(tab);
    }

    if (value) {
      this.redirectToValue(value);
    }
  }
}

export default TabbedContainer;
